import { useEffect, useRef, useState, type ReactNode } from 'react';
import { AlertCircleIcon, CheckIcon, LoaderCircleIcon, MinusIcon } from 'lucide-react';
import { sourceReferenceKey, type ActivityTrace, type ActivityTraceStep } from './activity-trace';
import { ToolData, ToolInputData } from './tool-data';
import { failedActivityLabel } from './activity-summary';
import { useChatCopy, useChatInterfaceLocale } from '@/lib/chat-i18n';
import { formatCount } from '@/i18n/format';
import { useNamespaceCopy } from '@/i18n/ns';
import artifactPanelsCopy from '@/i18n/ns/artifact-panels';
import chatErrorCopy from '@/i18n/ns/chat-error';
import { quoteErrorMessage } from '@/lib/chat-error-message';
import appVersionsCopy from '@/i18n/ns/app-versions';
import { appSaveRefusal } from '@/workspace/apps/versions/save-refusal';
import { saveArtifactState } from './tool-presentation';

export type ActivitySource = { id: string; href?: string };

const ALSO_READ: Record<string, string> = {
  en: 'Also read',
  sv: 'Även lästa',
  da: 'Også læst',
  de: 'Ebenfalls gelesen',
  es: 'También leído',
  fr: 'Également consulté',
  it: 'Letto anche',
  nl: 'Ook gelezen',
  pt: 'Também lido',
  pl: 'Przeczytano także',
  fi: 'Myös luettu',
  cs: 'Také přečteno',
  el: 'Επίσης αναγνώστηκαν',
  ro: 'Citite de asemenea',
  hu: 'Szintén elolvasva',
  sk: 'Prečítané tiež',
  sl: 'Tudi prebrano',
  hr: 'Također pročitano',
  bg: 'Също прочетени',
  et: 'Samuti loetud',
  lt: 'Taip pat perskaityta',
  lv: 'Arī izlasīts',
  mt: 'Aqra wkoll',
  ga: 'Léite freisin',
};

/** Measured durations only, in the reader's locale (e.g. "0.8 s"). A step faster
 * than the display precision reads "<0.1 s", never an invented 0.1 s. */
function durationLabel(ms: number, locale: string) {
  const seconds = new Intl.NumberFormat(locale, {
    style: 'unit',
    unit: 'second',
    unitDisplay: 'narrow',
    maximumFractionDigits: ms < 10_000 ? 1 : 0,
  });
  return ms < 100 ? `<${seconds.format(0.1)}` : seconds.format(ms / 1000);
}

/**
 * What a step looked at, in the reader's words (ACTIONS-DESIGN §5.2): a search
 * reads as its quoted query, a read as the title of what it opened, a web read as
 * its address. Paging and size arguments (limit, offset, length) and unresolved
 * ids stay in the expanded Input block. Keys are the tools' exact argument names.
 */
function stepSubject(
  step: ActivityTraceStep,
  resolveTitle?: (id: string) => string | null,
): { text: string; query: boolean } | null {
  let args: unknown;
  try {
    args = step.argsText ? JSON.parse(step.argsText) : null;
  } catch {
    return null;
  }
  if (!args || typeof args !== 'object' || Array.isArray(args)) return null;
  const { query, need, id, url } = args as Record<string, unknown>;
  if (typeof query === 'string' && query.trim()) return { text: `“${query.trim()}”`, query: true };
  // find_right_skill names what the model needed, in its own words.
  if (typeof need === 'string' && need.trim()) return { text: `“${need.trim()}”`, query: true };
  const title = typeof id === 'string' ? resolveTitle?.(id) : null;
  if (title) return { text: title, query: false };
  if (typeof url === 'string') {
    try {
      const address = new URL(url);
      return { text: address.hostname + address.pathname.replace(/\/$/, ''), query: false };
    } catch {
      return null;
    }
  }
  return null;
}

const STEP_GLYPHS = {
  error: AlertCircleIcon,
  cancelled: MinusIcon,
  incomplete: MinusIcon,
  running: LoaderCircleIcon,
} as const;

/** Canvas-ready, evidence-only trace renderer. Styling belongs to the polish pass. */
export function ActivityTraceView({
  trace,
  question,
  tab = 'steps',
  onTabChange,
  sourceContent,
  readOnly = false,
  step,
  resolveTitle,
  stepDetail,
}: {
  trace: ActivityTrace | null;
  question?: string | null;
  tab?: 'steps' | 'sources';
  onTabChange?: (tab: 'steps' | 'sources') => void;
  sourceContent?: (source: ActivitySource) => ReactNode;
  readOnly?: boolean;
  step?: string;
  /** The reader-facing title of an object id a step read, when the canvas knows it. */
  resolveTitle?: (id: string) => string | null;
  /**
   * A tool's own evidence for an expanded step, in the reader's words, in
   * place of the generic Input and Output blocks; null keeps them.
   */
  stepDetail?: (step: ActivityTraceStep) => ReactNode | null;
}) {
  const copy = useChatCopy();
  const panels = useNamespaceCopy(artifactPanelsCopy);
  const errorCopy = useNamespaceCopy(chatErrorCopy);
  const versionsCopy = useNamespaceCopy(appVersionsCopy);
  const displayLocale = useChatInterfaceLocale();
  const locale = displayLocale.split('-')[0];
  const [activeTab, setActiveTab] = useState(tab);
  const [expanded, setExpanded] = useState<string | null>(step ?? null);
  const stepButtons = useRef<Array<HTMLButtonElement | null>>([]);
  useEffect(() => setActiveTab(tab), [tab]);
  useEffect(() => setExpanded(step ?? null), [step]);
  const selectTab = (next: 'steps' | 'sources') => {
    setActiveTab(next);
    onTabChange?.(next);
  };
  if (!trace) return <p data-slot="activity-trace-unavailable">{copy.activityUnavailable}</p>;
  const citedIds = trace.citedSourceIds ?? trace.sourceIds;
  const sourceLinks = new Map<string, ActivitySource>();
  for (const link of trace.sourceLinks ?? []) {
    const key = sourceReferenceKey(link);
    const previous = sourceLinks.get(key);
    sourceLinks.set(key, previous ? { ...previous, ...link } : link);
  }
  const linkedIds = new Set((trace.sourceLinks ?? []).map((link) => link.id));
  for (const id of trace.sourceIds) {
    // A canonical link may carry the same server id while retaining its
    // revision and kind. Do not add a second bare-id row for that evidence.
    if (!linkedIds.has(id)) sourceLinks.set(sourceReferenceKey(id), { id });
  }
  for (const href of trace.citedSourceIds ?? []) {
    const key = sourceReferenceKey(href);
    const previous = sourceLinks.get(key);
    sourceLinks.set(key, previous ? { ...previous, href } : { id: href, href });
  }
  const sources = [...sourceLinks.values()];
  const citedKeys = new Set(citedIds.map((id) => sourceReferenceKey(id)));
  const cited = sources.filter((source) => citedKeys.has(sourceReferenceKey(source)));
  const alsoRead = sources.filter((source) => !citedKeys.has(sourceReferenceKey(source)));
  const earlierCount = trace.steps.filter((step) => step.earlier).length;
  return (
    <div data-slot="activity-trace-view" data-outcome={trace.outcome}>
      {question && <p data-slot="activity-trace-question">{question}</p>}
      <div role="tablist" aria-label={copy.activityOpen}>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'steps'}
          onClick={() => selectTab('steps')}
        >
          {copy.activitySteps}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'sources'}
          onClick={() => selectTab('sources')}
        >
          {copy.activitySourcesTab} {citedIds.length}
        </button>
      </div>
      {activeTab === 'sources' ? (
        <div role="tabpanel" data-slot="activity-trace-sources">
          {cited.map((source) => (
            <div key={source.id} data-source-id={source.id}>
              {sourceContent?.(source)}
            </div>
          ))}
          {citedIds.length === 0 && <p>{copy.activityNoSources}</p>}
          {alsoRead.length > 0 && (
            <div data-slot="activity-trace-also-read">
              <h3>{ALSO_READ[locale] ?? ALSO_READ.en}</h3>
              {alsoRead.map((source) => (
                <div key={source.id} data-source-id={source.id}>
                  {sourceContent?.(source)}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div
          role="tabpanel"
          data-slot="activity-trace-steps"
          onKeyDown={(event) => {
            if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
            const current = stepButtons.current.indexOf(
              document.activeElement as HTMLButtonElement,
            );
            if (current < 0) return;
            event.preventDefault();
            const next =
              event.key === 'Home'
                ? 0
                : event.key === 'End'
                  ? trace.steps.length - 1
                  : (current + (event.key === 'ArrowDown' ? 1 : -1) + trace.steps.length) %
                    trace.steps.length;
            stepButtons.current[next]?.focus();
          }}
        >
          {earlierCount > 0 && (
            <p data-slot="activity-trace-earlier">
              {formatCount(copy.toolEarlierAttemptsFailed, earlierCount, displayLocale)}
            </p>
          )}
          {trace.steps.map((step, index) => {
            const isOpen = expanded === step.id;
            const subject = stepSubject(step, resolveTitle);
            const results =
              subject?.query && step.resultCount != null
                ? formatCount(copy.activityResultsCount, step.resultCount, displayLocale)
                : '';
            const subline = [subject?.text, results].filter(Boolean).join(' · ');
            const detail = isOpen ? (stepDetail?.(step) ?? null) : null;
            // A known refusal is the whole output of its step: said once, in the person's language.
            const refused = (step.result as { error?: unknown } | null)?.error;
            const reason =
              (step.isError ? quoteErrorMessage(refused, errorCopy) : null) ??
              // An App save refused for its tables answers with a result, not a thrown error.
              (step.outcome === 'error' ? appSaveRefusal(refused, versionsCopy) : null);
            return (
              <div
                key={step.id}
                data-slot="activity-trace-step"
                data-outcome={step.outcome}
                data-earlier={step.earlier ? 'true' : undefined}
              >
                <button
                  type="button"
                  ref={(element) => {
                    stepButtons.current[index] = element;
                  }}
                  aria-expanded={isOpen}
                  aria-controls={`activity-step-${step.id}`}
                  onClick={() => setExpanded(isOpen ? null : step.id)}
                >
                  <span aria-hidden data-slot="activity-trace-glyph">
                    <StepGlyph outcome={step.outcome} />
                  </span>
                  <span>
                    {step.outcome === 'error'
                      ? (step.failedLabel ?? failedActivityLabel(step.toolName, displayLocale))
                      : step.label}
                    {subline && <small data-slot="activity-trace-summary">{subline}</small>}
                  </span>
                  {step.durationMs !== undefined && (
                    <time>{durationLabel(step.durationMs, displayLocale)}</time>
                  )}
                </button>
                {reason && <p data-slot="activity-trace-reason">{reason}</p>}
                {/* Visible without opening the step: we cannot say whether this save happened. */}
                {saveArtifactState(step.toolName, step.result) === 'unconfirmed' && (
                  <p role="status" data-slot="activity-trace-unconfirmed">
                    {panels.finishUnconfirmed}
                  </p>
                )}
                {isOpen && (
                  <div id={`activity-step-${step.id}`} data-slot="activity-trace-detail">
                    {step.pack && <p>{step.pack}</p>}
                    {detail ?? (
                      <>
                        {step.argsText && (
                          <div data-slot="activity-trace-input">
                            <h3>{copy.activityInput}</h3>
                            <ToolInputData text={step.argsText} />
                          </div>
                        )}
                        {step.text && <p data-slot="activity-trace-reasoning">{step.text}</p>}
                        {step.result !== undefined && !reason && (
                          <div data-slot="activity-trace-output">
                            <h3>{copy.activityOutput}</h3>
                            <ToolData value={step.result} />
                          </div>
                        )}
                      </>
                    )}
                    {step.approval != null &&
                      typeof step.approval === 'object' &&
                      'text' in step.approval && (
                        <p data-slot="activity-trace-approval">
                          {copy.activityApprovedByReply} “
                          {String((step.approval as { text: unknown }).text)}”
                        </p>
                      )}
                    {step.sourceIds.map((id) => {
                      const source =
                        sourceLinks.get(sourceReferenceKey(id)) ?? ({ id } as ActivitySource);
                      return (
                        <div key={id} data-source-id={id}>
                          {sourceContent?.(source)}
                        </div>
                      );
                    })}
                    {readOnly && Boolean(step.approval) && <p>{copy.activityApprovedByReply}</p>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StepGlyph({ outcome }: { outcome: string }) {
  const Glyph = STEP_GLYPHS[outcome as keyof typeof STEP_GLYPHS] ?? CheckIcon;
  return <Glyph aria-hidden />;
}
